// react_frontend/src/Components/Modules/Category/AddCategory.js
import React, { useState, useRef, useContext, useEffect } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { MotherContext } from "../../../Context/MotherContext";
// RSuite Components
import { Form, Button, Message, Loader, Whisper, Tooltip, Schema, SelectPicker, toaster, Modal, Input } from "rsuite";
import "rsuite/dist/rsuite.min.css";
// Icons
import { FaBox, FaTags, FaClipboardList, FaPlus } from "react-icons/fa";

const AddItem = () => {
  const navigate = useNavigate();
  const { addItemToList, categories, startCategoryPolling, stopCategoryPolling } = useContext(MotherContext);

  useEffect(() => {
    startCategoryPolling();
    return () => stopCategoryPolling();
  }, [startCategoryPolling, stopCategoryPolling]);

  const [formValue, setFormValue] = useState({
    category_id: "",
    item_name: "",
    product_unit: "",
  });
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef();

  // ✅ Default unit list (static)
  const defaultUnitData = [
    { label: "Piece (Pcs)", value: "Pcs" },
    { label: "Set", value: "Set" },
    { label: "Dozen", value: "Dozen" },
    { label: "Packet", value: "Packet" },
    { label: "Box", value: "Box" },
    { label: "Carton", value: "Carton" },
    { label: "Bag", value: "Bag" },
    { label: "Roll", value: "Roll" },
    { label: "Bundle", value: "Bundle" },
    { label: "Unit", value: "Unit" },
    { label: "Plate", value: "Plate" },
    { label: "Portion", value: "Portion" },
    { label: "Cup", value: "Cup" },
    { label: "Bottle", value: "Bottle" },
    { label: "Glass", value: "Glass" },
    { label: "Slice", value: "Slice" },
    { label: "Bowl", value: "Bowl" },
    { label: "Tablet", value: "Tablet" },
    { label: "Capsule", value: "Capsule" },
    { label: "Strip", value: "Strip" },
    { label: "Tube", value: "Tube" },
    { label: "Drop", value: "Drop" },
    { label: "Ampoule", value: "Ampoule" },
    { label: "Vial", value: "Vial" },
    { label: "Jar", value: "Jar" },
    { label: "Gram (g)", value: "Gram" },
    { label: "Kilogram (kg)", value: "Kilogram" },
    { label: "Milligram (mg)", value: "Milligram" },
    { label: "Tonne (ton)", value: "Tonne" },
    { label: "Pound (lb)", value: "Pound" },
    { label: "Ounce (oz)", value: "Ounce" },
    { label: "Liter (L)", value: "Liter" },
    { label: "Milliliter (mL)", value: "Milliliter" },
    { label: "Gallon", value: "Gallon" },
    { label: "Pint", value: "Pint" },
    { label: "Quart", value: "Quart" },
    { label: "Barrel", value: "Barrel" },
    { label: "Sheet", value: "Sheet" },
    { label: "Coil", value: "Coil" },
    { label: "Rod", value: "Rod" },
    { label: "Bar", value: "Bar" },
    { label: "Square Meter (m²)", value: "Square Meter" },
    { label: "Square Foot (ft²)", value: "Square Foot" },
    { label: "N/A", value: "N/A" },
  ].sort((a, b) => a.label.localeCompare(b.label));

  // ✅ State for dynamic unit options (default + from DB)
  const [unitOptions, setUnitOptions] = useState([
    { label: "➕ Add your own unit", value: "custom" },
    ...defaultUnitData,
  ]);

  const [showUnitModal, setShowUnitModal] = useState(false);
  const [customUnit, setCustomUnit] = useState("");

  // 🔹 Fetch unique units from items (DB)
  useEffect(() => {
    const fetchUniqueUnits = async () => {
      try {
        const res = await AxiosAuthInstance.get("add_item");
        const items = res.data.item || [];

        // Extract unique product_unit values
        const uniqueUnits = [...new Set(items.map(item => item.product_unit).filter(Boolean))];

        // Normalize and merge with defaults
        const seen = new Set(defaultUnitData.map(u => u.value.toLowerCase()));
        const dbUnits = [];

        uniqueUnits.forEach(unit => {
          const normalized = unit.trim();
          if (normalized && !seen.has(normalized.toLowerCase())) {
            seen.add(normalized.toLowerCase());
            dbUnits.push({ label: normalized, value: normalized });
          }
        });

        // Sort DB units
        dbUnits.sort((a, b) => a.label.localeCompare(b.label));

        // Combine
        const allUnits = [
          { label: "➕ Add your own unit", value: "custom" },
          ...defaultUnitData,
          ...dbUnits,
        ];

        setUnitOptions(allUnits);
      } catch (err) {
        console.error("Failed to fetch unique units", err);
        // Fallback to default
        setUnitOptions([
          { label: "➕ Add your own unit", value: "custom" },
          ...defaultUnitData,
        ]);
      }
    };

    fetchUniqueUnits();
  }, []);

  // 🔹 Handle custom unit add
  const handleAddCustomUnit = () => {
    let newUnit = customUnit.trim();
    if (!newUnit) return;

    // Normalize: First letter uppercase, rest lowercase
    newUnit = newUnit.charAt(0).toUpperCase() + newUnit.slice(1).toLowerCase();

    // ✅ Check against ALL existing units (case-insensitive)
    const existingValues = unitOptions
      .filter(u => u.value !== "custom")
      .map(u => u.value.toLowerCase());

    if (existingValues.includes(newUnit.toLowerCase())) {
      toaster.push(
        <Message type="warning" duration={3000}>
          "{newUnit}" already exists!
        </Message>,
        { placement: "topEnd" }
      );
      return;
    }

    // ✅ Add to local state
    const newUnitObj = { label: newUnit, value: newUnit };
    const updatedUnits = [...unitOptions.filter(u => u.value !== "custom"), newUnitObj];
    updatedUnits.sort((a, b) => a.label.localeCompare(b.label));

    setUnitOptions([{ label: "➕ Add your own unit", value: "custom" }, ...updatedUnits]);
    setFormValue(prev => ({ ...prev, product_unit: newUnit }));
    setCustomUnit("");
    setShowUnitModal(false);

    toaster.push(
      <Message type="success" duration={2000}>
        Custom unit "{newUnit}" added to list. Will save with item.
      </Message>,
      { placement: "topEnd" }
    );
  };

  // 🔹 Cancel modal
  const handleCancelUnitModal = () => {
    setCustomUnit("");
    setShowUnitModal(false);
    setFormValue(prev => ({ ...prev, product_unit: "" }));
  };

  const activeCategories = categories
    .filter(cat => cat.category_status === 1)
    .sort((a, b) => a.category_name.localeCompare(b.category_name));

  const { StringType } = Schema.Types;
  const model = Schema.Model({
    category_id: StringType().isRequired("Category is required."),
    item_name: StringType().isRequired("Item name is required."),
    product_unit: StringType().isRequired("Unit is required."),
  });

  const handleSubmit = async () => {
    if (!formRef.current.check()) return;

    setLoading(true);
    try {
      const payload = {
        category_id: formValue.category_id,
        item_name: formValue.item_name,
        product_unit: formValue.product_unit,
      };

      const res = await AxiosAuthInstance.post("add_item", payload);

      if (res.data.status === true) {
        toaster.push(
          <Message type="success" duration={3000}>
            Item added successfully!
          </Message>,
          { placement: "topEnd" }
        );
        addItemToList(res.data.item);
        navigate("/item_list");
      } else {
        toaster.push(
          <Message type="error" duration={5000}>
            {res.data.message || "Failed to add item."}
          </Message>,
          { placement: "topEnd" }
        );
      }
    } catch (err) {
      console.error("Add item error:", err);
      const serverErrors = err.response?.data?.errors || {};
      if (serverErrors.limit) {
        toaster.push(
          <Message type="warning" duration={5000}>
            {serverErrors.limit[0]}
          </Message>,
          { placement: "topEnd" }
        );
      } else {
        const errorMsg =
          err.response?.data?.message ||
          Object.values(serverErrors)[0]?.[0] ||
          "Failed to add item.";
        toaster.push(
          <Message type="error" duration={5000}>
            {errorMsg}
          </Message>,
          { placement: "topEnd" }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/item_list");
  };

  return (
    <>
      <Breadcrumb title="Add Item" />

      <div className="p-3">
        <div className="card shadow-sm border-0 mx-auto" style={{ maxWidth: "700px" }}>
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaPlus /> Add New Item
            </h5>
            <Link to="/item_list" className="text-white text-decoration-none">
              <Whisper placement="left" speaker={<Tooltip>Item List</Tooltip>}>
                <Button size="sm" appearance="subtle" className="text-white">
                  <FaClipboardList /> List
                </Button>
              </Whisper>
            </Link>
          </div>

          <div className="card-body bg-light p-4">
            <Form
              ref={formRef}
              fluid
              formValue={formValue}
              formError={formError}
              onChange={setFormValue}
              onCheck={setFormError}
              model={model}
            >
              {/* Category */}
              <Form.Group controlId="category_id">
                <Form.ControlLabel>Category *</Form.ControlLabel>
                <Form.Control
                  name="category_id"
                  accepter={SelectPicker}
                  data={activeCategories.map((cat) => ({
                    label: (
                      <div className="d-flex align-items-center gap-2">
                        <FaTags /> <span>{cat.category_name}</span>
                      </div>
                    ),
                    value: cat.id.toString(),
                  }))}
                  valueKey="value"
                  labelKey="label"
                  placeholder="Select Category"
                  style={{ width: "100%" }}
                  renderMenuOnBody
                  cleanable={false}
                />
                <Form.ErrorMessage name="category_id" />
              </Form.Group>

              {/* Item Name */}
              <Form.Group controlId="item_name">
                <Form.ControlLabel>Item Name *</Form.ControlLabel>
                <Form.Control
                  name="item_name"
                  placeholder="e.g., iPhone 15"
                  prefix={<FaBox />}
                />
                <Form.ErrorMessage name="item_name" />
              </Form.Group>

              {/* Product Unit */}
              <Form.Group controlId="product_unit">
                <Form.ControlLabel>Unit *</Form.ControlLabel>
                <Form.Control
                  name="product_unit"
                  accepter={SelectPicker}
                  data={unitOptions}
                  placeholder="Select Unit"
                  style={{ width: "100%" }}
                  renderMenuOnBody
                  cleanable={false}
                  value={formValue.product_unit}
                  onChange={(value) => {
                    if (value === "custom") {
                      setShowUnitModal(true);
                    } else {
                      setFormValue(prev => ({ ...prev, product_unit: value }));
                    }
                  }}
                />
                <Form.ErrorMessage name="product_unit" />
              </Form.Group>

              {/* Custom Unit Modal */}
              <Modal
                open={showUnitModal}
                onClose={handleCancelUnitModal}
                size="xs"
                backdrop="static"
              >
                <Modal.Header>
                  <Modal.Title>Add Your Custom Unit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Input
                    placeholder="Enter your unit name (e.g. Box, Bundle...)"
                    value={customUnit}
                    onChange={setCustomUnit}
                    autoFocus
                  />
                </Modal.Body>
                <Modal.Footer>
                  <Button appearance="primary" onClick={handleAddCustomUnit}>
                    Add Unit
                  </Button>
                  <Button appearance="subtle" onClick={handleCancelUnitModal}>
                    Cancel
                  </Button>
                </Modal.Footer>
              </Modal>

              {/* Action Buttons */}
              <div className="d-flex gap-3 pt-3 border-top" style={{ marginTop: "2rem" }}>
                <Button
                  appearance="primary"
                  color="blue"
                  size="md"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? (
                    <>
                      <Loader size="sm" /> Adding...
                    </>
                  ) : (
                    "Add Item"
                  )}
                </Button>

                <Button
                  appearance="subtle"
                  color="red"
                  size="md"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{
                    flex: 1,
                    border: "1px solid #dc3545",
                    color: "#dc3545",
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddItem;